import { useAuthData } from '@auth/features/auth';
import { TasksTable } from '@task-manager/widgets/tasks-table';
import { SlidePanel } from '@/shared/ui';
import { useTaskDrawerStore } from '../model/use-task-drawer-store';
import css from './task-drawer.module.scss';

/**
 * Mounted once at the AppLayout root so it overlays every page. State
 * lives in `useTaskDrawerStore`; the topbar trigger (and any itinerary
 * header trigger) call `openDrawer({ scope? })` and the panel renders
 * here. The body reuses the full tasks-table widget pre-filtered by
 * scope — matches legacy parity where the drawer surfaced the same
 * table the dedicated page uses.
 *
 * Width is 1300px to match legacy (`w-[1300px]` Sheet). On viewports
 * narrower than that, SlidePanel clamps to the viewport.
 */
export const TaskDrawer = () => {
  const open = useTaskDrawerStore((s) => s.open);
  const scope = useTaskDrawerStore((s) => s.scope);
  const closeDrawer = useTaskDrawerStore((s) => s.closeDrawer);

  const { data: authData } = useAuthData();
  const currentUserId = authData?.user.id;

  // Scope resolution mirrors legacy: itineraryId → tasks for that
  // itinerary; no scope → current user's tasks. We pass scope through
  // to the table so it server-side-filters without polluting the URL
  // of the page underneath the drawer.
  const tableScope = scope.itineraryId
    ? { itineraryId: scope.itineraryId }
    : scope.assignedAccountId
      ? { assignedAccountId: scope.assignedAccountId }
      : currentUserId
        ? { assignedAccountId: currentUserId }
        : undefined;

  return (
    <SlidePanel
      onClose={closeDrawer}
      open={open}
      title={scope.itineraryId ? 'Itinerary tasks' : 'My tasks'}
      width={1300}
    >
      <div className={css.body}>
        {tableScope ? <TasksTable scope={tableScope} /> : null}
      </div>
    </SlidePanel>
  );
};
